import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const FLAG_SEARCH_QUERY = 'title:flag -title:post-it -title:pin -title:decal -title:shirt -title:t-shirt -title:sweatshirt -title:hoodie';
const LAB_COAT_SEARCH_QUERY = 'title:coat';
const GUARDED_SEARCH_QUERIES = new Set([FLAG_SEARCH_QUERY, LAB_COAT_SEARCH_QUERY]);
const CQ_CATEGORY_FILTER_PARAM = 'filter.p.m.characterquilt.shopping_category';
const CQ_TSHIRT_CATEGORY_VALUES = ['T-Shirts & Tanks', 'T-Shirts', 'Tanks'];

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const categoryValues = newParameters.getAll(CQ_CATEGORY_FILTER_PARAM);
    if (categoryValues.includes(CQ_TSHIRT_CATEGORY_VALUES[0])) {
      for (const categoryValue of CQ_TSHIRT_CATEGORY_VALUES.slice(1)) {
        if (!categoryValues.includes(categoryValue)) newParameters.append(CQ_CATEGORY_FILTER_PARAM, categoryValue);
      }
    }

    const sizeValues = newParameters.getAll(CQ_SIZE_FACET_PARAM);
    for (const [canonicalSize, legacySize] of cqSizeAliasByCanonical) {
      if (sizeValues.includes(canonicalSize) && !sizeValues.includes(legacySize)) {
        newParameters.append(CQ_SIZE_FACET_PARAM, legacySize);
      }
    }

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    // Always ensure availability filter is set to "In stock" (value "1")
    newParameters.set('filter.v.availability', '1');

    return newParameters;
  }

  /**
   * Gets the search query: prefers the search input field value (if populated),
   * otherwise falls back to the current URL.
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    const currentQuery = url.searchParams.get(SEARCH_QUERY) ?? '';
    if (GUARDED_SEARCH_QUERIES.has(currentQuery)) return currentQuery;
    const searchInput = document.querySelector('input[name="q"]:not([type="hidden"])');
    if (searchInput instanceof HTMLInputElement) {
      const value = searchInput.value?.trim() ?? '';
      if (value) return value;
    }
    return currentQuery;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Builds the full URL for the current filter parameters
   * @returns {string} The full URL with filter parameters
   */
  #buildFilterUrl() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    return url.toString();
  }

  /**
   * Updates filters by redirecting to the requested URL
   */
  updateFilters = () => {
    const targetUrl = this.#buildFilterUrl();
    if (targetUrl !== window.location.href) {
      window.location.href = targetUrl;
    }
  };

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    // Parse the URL and ensure required filters are always present
    const urlObj = new URL(url, window.location.origin);
    const currentUrl = new URL(window.location.href);
    
    // Check if this is a search page or collection page
    const isSearchPage = currentUrl.pathname === '/search' || currentUrl.pathname.startsWith('/search/');
    const isCollectionPage = currentUrl.pathname.startsWith('/collections/');
    
    // For search pages, always ensure pathname is /search
    if (isSearchPage) {
      urlObj.pathname = '/search';
      
      // Preserve search query: prefer the search input value if populated, else URL
      const searchQuery =
        document.querySelector('input[name="q"]:not([type="hidden"])')?.value?.trim() ||
        currentUrl.searchParams.get(SEARCH_QUERY) ||
        '';
      if (searchQuery) {
        urlObj.searchParams.set(SEARCH_QUERY, searchQuery);
      }
      
      // Preserve search options only for search pages
      const optionsPrefix = currentUrl.searchParams.get('options[prefix]');
      if (optionsPrefix) {
        urlObj.searchParams.set('options[prefix]', optionsPrefix);
      }
    }
    
    // Preserve sort options if they exist in current URL (for both search and collection)
    const sortBy = currentUrl.searchParams.get('sort_by');
    if (sortBy) {
      urlObj.searchParams.set('sort_by', sortBy);
    }
    
    // Always ensure availability filter is set to "In stock" (value "1")
    urlObj.searchParams.set('filter.v.availability', '1');
    
    // Redirect to the modified URL
    const finalUrl = urlObj.pathname + urlObj.search + urlObj.hash;
    if (finalUrl !== window.location.pathname + window.location.search + window.location.hash) {
      window.location.href = finalUrl;
    }
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}


/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} minSlider - The minimum price slider
 * @property {HTMLInputElement} maxSlider - The maximum price slider
 * @property {HTMLElement} sliderRange - The slider range element
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {AbortController | undefined} */
  #rangeCheckboxAbort;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    
    const { minSlider, maxSlider, minInput, maxInput } = this.refs;
    
    // Min/max input listeners (for slider sync and for range-checkbox sync)
    if (minInput && maxInput) {
      minInput.addEventListener('input', this.#onMinMaxInputChange);
      maxInput.addEventListener('input', this.#onMinMaxInputChange);
    }
    
    // Slider event listeners (optional)
    if (minSlider && maxSlider) {
      minSlider.addEventListener('input', this.#onSliderInput);
      maxSlider.addEventListener('input', this.#onSliderInput);
      minInput?.addEventListener('input', this.#onInputChange);
      maxInput?.addEventListener('input', this.#onInputChange);
      this.#updateSliderRange();
    }
    
    // Price range checkboxes: when checked, set min/max inputs and update URL
    this.#bindPriceRangeCheckboxes();
    
    // Listen for filter updates to re-sync checkboxes after re-render
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    
    const { minSlider, maxSlider, minInput, maxInput } = this.refs;
    
    if (minInput && maxInput) {
      minInput.removeEventListener('input', this.#onMinMaxInputChange);
      maxInput.removeEventListener('input', this.#onMinMaxInputChange);
    }
    
    if (minSlider && maxSlider) {
      minSlider.removeEventListener('input', this.#onSliderInput);
      maxSlider.removeEventListener('input', this.#onSliderInput);
      minInput?.removeEventListener('input', this.#onInputChange);
      maxInput?.removeEventListener('input', this.#onInputChange);
    }
    
    this.#rangeCheckboxAbort?.abort();
  }

  /**
   * Handles slider input events
   * @param {Event} event - The input event
   */
  #onSliderInput = (event) => {
    const { minSlider, maxSlider, minInput, maxInput } = this.refs;
    
    let minVal = Number(minSlider.value);
    let maxVal = Number(maxSlider.value);
    
    // Prevent sliders from crossing
    if (minVal > maxVal) {
      if (event.target === minSlider) {
        minSlider.value = maxVal.toString();
        minVal = maxVal;
      } else {
        maxSlider.value = minVal.toString();
        maxVal = minVal;
      }
    }
    
    // Update text inputs
    minInput.value = minVal.toString();
    maxInput.value = maxVal.toString();
    
    // Update visual range
    this.#updateSliderRange();
  };

  /**
   * Bind change listeners to price range checkboxes and sync initial state
   */
  #bindPriceRangeCheckboxes() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;
    
    const wrappers = this.querySelectorAll('.price-facet__range-checkbox-wrapper');
    if (!wrappers.length) return;
    
    this.#rangeCheckboxAbort = new AbortController();
    const { signal } = this.#rangeCheckboxAbort;
    
    const rangeMax = Number(this.getAttribute('data-range-max')) || 0;
    
    wrappers.forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;
      const checkbox = wrapper.querySelector('input[type="checkbox"]');
      if (!(checkbox instanceof HTMLInputElement)) return;
      
      const min = wrapper.dataset.min ?? '0';
      const max = wrapper.dataset.max ?? String(rangeMax);
      const isPlusRange = wrapper.dataset.plusRange === 'true';
      checkbox.dataset.min = min;
      checkbox.dataset.max = max;
      checkbox.dataset.plusRange = isPlusRange ? 'true' : 'false';
      checkbox.classList.add('price-facet__range-checkbox');
      
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          minInput.value = min;
          const minNum = Number(min) || 0;
          const maxNum = Number(max);

          // For open-ended ranges (e.g. $150+) or invalid caps, keep max empty.
          if (isPlusRange || Number.isNaN(maxNum) || minNum > maxNum) {
            maxInput.value = '';
          } else {
            maxInput.value = max;
          }
          wrappers.forEach((otherWrapper) => {
            if (!(otherWrapper instanceof HTMLElement)) return;
            const otherCheckbox = otherWrapper.querySelector('input[type="checkbox"]');
            if (otherCheckbox instanceof HTMLInputElement && otherCheckbox !== checkbox) {
              otherCheckbox.checked = false;
            }
          });
          const facetInputsComponent = checkbox.closest('facet-inputs-component');
          if (facetInputsComponent instanceof FacetInputsComponent) {
            facetInputsComponent.updateFilters();
          }
        } else {
          minInput.value = '';
          maxInput.value = '';
          const facetInputsComponent = checkbox.closest('facet-inputs-component');
          if (facetInputsComponent instanceof FacetInputsComponent) {
            facetInputsComponent.updateFilters();
          }
        }
      }, { signal });
    });
    
    this.#syncCheckboxStates();
  }

  /**
   * Sync checkbox states based on current min/max input values
   */
  #syncCheckboxStates() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;
    
    const wrappers = this.querySelectorAll('.price-facet__range-checkbox-wrapper');
    if (!wrappers.length) return;
    
    const rangeMax = Number(this.getAttribute('data-range-max')) || 0;
    const currentMin = Math.round(Number(minInput.value) || 0);
    const currentMax = Math.round(Number(maxInput.value) || rangeMax);
    
    wrappers.forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;
      const checkbox = wrapper.querySelector('input[type="checkbox"]');
      if (!(checkbox instanceof HTMLInputElement)) return;
      const min = Number(wrapper.dataset.min ?? 0);
      const max = Math.round(Number(wrapper.dataset.max ?? rangeMax));
      const isPlusRange = wrapper.dataset.plusRange === 'true';
      if (isPlusRange) {
        checkbox.checked = (currentMin === min && !maxInput.value);
      } else {
        checkbox.checked = (currentMin === min && currentMax === max);
      }
    });
  }

  /**
   * Handle filter update event to re-sync checkboxes after re-render
   */
  #handleFilterUpdate = () => {
    // Delay slightly to ensure inputs are populated after re-render
    setTimeout(() => {
      this.#syncCheckboxStates();
    }, 50);
  };

  /**
   * Handles min/max text input: uncheck price range checkboxes; if sliders exist, sync them
   */
  #onMinMaxInputChange = () => {
    this.querySelectorAll('.price-facet__range-checkbox-wrapper input[type="checkbox"]').forEach((cb) => {
      if (cb instanceof HTMLInputElement) cb.checked = false;
    });
    const { minSlider, maxSlider, minInput, maxInput } = this.refs;
    if (minSlider && maxSlider && minInput && maxInput) {
      const minVal = Number(minInput.value) || 0;
      const maxVal = Number(maxInput.value) || Number(this.getAttribute('data-range-max'));
      minSlider.value = minVal.toString();
      maxSlider.value = maxVal.toString();
      this.#updateSliderRange();
    }
  };

  /**
   * Handles text input change events (slider sync only)
   */
  #onInputChange = () => {
    const { minSlider, maxSlider, minInput, maxInput } = this.refs;
    
    const minVal = Number(minInput.value) || 0;
    const maxVal = Number(maxInput.value) || Number(this.getAttribute('data-range-max'));
    
    minSlider.value = minVal.toString();
    maxSlider.value = maxVal.toString();
    
    this.#updateSliderRange();
  };

  /**
   * Updates the visual range of the slider
   */
  #updateSliderRange() {
    const { minSlider, maxSlider, sliderRange } = this.refs;
    
    if (!sliderRange) return;
    
    const rangeMax = Number(this.getAttribute('data-range-max')) || 100;
    const minVal = Number(minSlider.value);
    const maxVal = Number(maxSlider.value);
    
    const minPercent = (minVal / rangeMax) * 100;
    const maxPercent = (maxVal / rangeMax) * 100;
    
    sliderRange.style.left = `${minPercent}%`;
    sliderRange.style.width = `${maxPercent - minPercent}%`;
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
    this.#updateSliderRange();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;
    const isFromPill =
      event.target instanceof HTMLElement && event.target.classList.contains('sorting-filter__pill-input');
    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    const selectElement = this.querySelector('select[name="sort_by"]');

    // Ensure only one sort_by source is used when building FormData:
    // - Mobile + pill clicked → disable select so the checked pill value is used.
    // - Mobile + select changed → disable pill radios so select value is used.
    // - Desktop → disable select so dropdown radios are used.
    if (shouldDisable) {
      if (isMobile && isFromPill) {
        if (selectElement instanceof HTMLSelectElement) selectElement.disabled = true;
      } else if (isMobile) {
        this.querySelectorAll('input[name="sort_by"]').forEach((input) => {
          if (input instanceof HTMLInputElement) input.disabled = true;
        });
      } else {
        if (selectElement instanceof HTMLSelectElement) selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    if (shouldDisable) {
      if (isMobile && isFromPill) {
        if (selectElement instanceof HTMLSelectElement) selectElement.disabled = false;
      } else if (isMobile) {
        this.querySelectorAll('input[name="sort_by"]').forEach((input) => {
          if (input instanceof HTMLInputElement) input.disabled = false;
        });
      } else {
        if (selectElement instanceof HTMLSelectElement) selectElement.disabled = false;
      }
    }

    // Close the panel when a value is selected (desktop dropdown or old mobile select). Keep pills panel open.
    if (!isFromPill) {
      const detailsToClose = event.target instanceof Element ? event.target.closest('details') : null;
      if (detailsToClose instanceof HTMLDetailsElement) detailsToClose.open = false;
    }
  }

  /**
   * Updates the facet status (desktop panel label). Supports select (#1) and radios: pills (#2) and desktop dropdown (#3).
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    const target = event.target;
    let value = '';
    let optionName = '';
    let defaultSortBy = '';

    if (target instanceof HTMLSelectElement) {
      value = target.value;
      optionName = target.options[target.selectedIndex]?.dataset?.optionName ?? target.options[target.selectedIndex]?.text ?? '';
    } else if (target instanceof HTMLInputElement && target.name === 'sort_by' && target.type === 'radio') {
      value = target.value;
      optionName = target.dataset.optionName ?? '';
    } else {
      return;
    }

    const detailsRef = this.refs?.details;
    const detailsWithStatus = (Array.isArray(detailsRef) ? detailsRef[0] : detailsRef) ?? this.querySelector('details[data-default-sort-by]');
    if (!(detailsWithStatus instanceof HTMLElement)) return;

    defaultSortBy = detailsWithStatus.dataset.defaultSortBy ?? '';
    const statusSpan = detailsWithStatus.querySelector('.facets__status');
    const facetStatus = detailsWithStatus.querySelector('facet-status-component');
    const statusEl = facetStatus instanceof FacetStatusComponent ? facetStatus : statusSpan;
    if (!statusEl) return;

    statusEl.textContent = value !== defaultSortBy ? optionName : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};


// Group shopper-facing Category, Color, and Size labels on search pages.
// Categories render immediately; avoid per-value search requests and delayed counts.
const CQ_CATEGORY_FACET_PARAM = 'filter.p.m.characterquilt.shopping_category';
const CQ_COLOR_FACET_PARAM = 'filter.p.m.characterquilt.color_family';
const CQ_SIZE_FACET_PARAM = 'filter.p.m.characterquilt.normalized_size';
const cqSizeDisplayLabels = new Map([
  ['XXS', 'XXSmall'], ['XS', 'XSmall'], ['S', 'Small'], ['M', 'Medium'],
  ['L', 'Large'], ['XL', 'XLarge'], ['2XL', '2XLarge'], ['3XL', '3XLarge'],
  ['4XL', '4XLarge'], ['5XL', '5XLarge'], ['6XL', '6XLarge'],
]);
const cqSizeAliasByCanonical = new Map([
  ['XXS', 'XXSmall'], ['XS', 'XSmall'], ['S', 'Small'], ['M', 'Medium'],
  ['L', 'Large'], ['XL', 'XLarge'], ['2XL', '2XLarge'], ['3XL', '3XLarge'],
  ['4XL', '4XLarge'], ['5XL', '5XLarge'], ['6XL', '6XLarge'],
]);
const CQ_CANONICAL_TSHIRT_CATEGORY = 'T-Shirts & Tanks';
const cqLegacyCategoryAliases = new Set(['T-Shirts', 'Tanks']);
const cqLegacySizeAliases = new Set([
  'XXSmall', 'XSmall', 'Small', 'Medium', 'Large', 'XLarge',
  '2XLarge', '3XLarge', '4XLarge', '5XLarge', '6XLarge',
]);

const cqCategoryLabelOverrides = new Map([
  ['gid://shopify/FilterSettingGroup/135233698', 'Accessories'],
  ['gid://shopify/FilterSettingGroup/135495842', 'Hats'],
  ['gid://shopify/FilterSettingGroup/135397538', 'Outerwear'],
  ['gid://shopify/FilterSettingGroup/135299234', 'Bottoms'],
  ['gid://shopify/FilterSettingGroup/135364770', 'T-Shirts & Tanks'],
]);

const cqCategoryServerLabelOverrides = new Map([
  ['Accessories & Jewelry', 'Accessories'],
  ['Outerwear & Layers', 'Outerwear'],
  ['Pants & Shorts', 'Bottoms'],
  ['Sweatshirts & Hoodies', 'Sweatshirts'],
  ['Dresses & Skirts', 'Dresses'],
]);

function cqIsInternalCategoryValue(value) {
  const normalizedValue = value.trim().toLowerCase();
  return (
    normalizedValue.startsWith('gid://') ||
    normalizedValue.startsWith('gid%3a%2f%2f') ||
    normalizedValue.startsWith('http://') ||
    normalizedValue.startsWith('https://') ||
    normalizedValue.startsWith('/admin/') ||
    normalizedValue.includes('shopify.com')
  );
}

function cqFacetCurrentLabel(input) {
  const labelElement = input.parentElement?.querySelector('.checkbox__label-text, .facets__pill-label, .facets__swatch-label') || input.closest('li')?.querySelector('label');
  return (labelElement?.textContent || input.dataset.label || input.value).trim().replace(/\s*\([^)]*\)\s*$/, '');
}

function cqCategoryDisplayLabel(input) {
  const currentLabel = cqFacetCurrentLabel(input);
  return cqCategoryLabelOverrides.get(input.value) || cqCategoryServerLabelOverrides.get(currentLabel) || currentLabel;
}

function cqHasScopedCategoryContext() {
  return ['/collections/men', '/collections/women', '/collections/electronics'].includes(window.location.pathname.replace(/\/$/, ''));
}

function cqCategoryAllowedForContext(label) {
  const path = window.location.pathname.replace(/\/$/, '');
  const allowedByPath = {
    '/collections/men': new Set(['Accessories', 'Bottoms', 'Hats', 'Jerseys', 'Outerwear', 'Polos', 'Sweatshirts', 'T-Shirts & Tanks']),
    '/collections/women': new Set(['Accessories', 'Dresses', 'Hats', 'Jerseys', 'Outerwear', 'Bottoms', 'Polos', 'Sweatshirts', 'T-Shirts & Tanks']),
    '/collections/electronics': new Set(['Technology']),
  };
  const allowed = allowedByPath[path];
  return !allowed || allowed.has(label);
}

function cqScopedCategoryOrder() {
  const path = window.location.pathname.replace(/\/$/, '');
  return {
    '/collections/men': ['Accessories', 'Bottoms', 'Hats', 'Jerseys', 'Outerwear', 'Polos', 'Sweatshirts', 'T-Shirts & Tanks'],
    '/collections/women': ['Accessories', 'Bottoms', 'Dresses', 'Hats', 'Jerseys', 'Outerwear', 'Polos', 'Sweatshirts', 'T-Shirts & Tanks'],
    '/collections/electronics': ['Technology'],
  }[path] || [];
}

function cqOrderScopedCategoryItems() {
  const order = cqScopedCategoryOrder();
  if (!order.length) return;
  const rank = new Map(order.map((label, index) => [label, index]));
  const itemsByList = new Map();
  for (const input of cqFacetInputs(CQ_CATEGORY_FACET_PARAM)) {
    const item = input.closest('li');
    if (!item?.parentElement) continue;
    const items = itemsByList.get(item.parentElement) || [];
    items.push({ item, rank: rank.get(cqCategoryDisplayLabel(input)) ?? Number.MAX_SAFE_INTEGER });
    itemsByList.set(item.parentElement, items);
  }
  for (const [list, items] of itemsByList) {
    for (const { item } of items.sort((a, b) => a.rank - b.rank)) list.appendChild(item);
  }
}

function cqSetCategoryInputHidden(input, hidden) {
  const item = input.closest('li');
  if (!item) return;
  if (hidden) {
    item.dataset.cqCategoryHidden = 'true';
    item.setAttribute('hidden', 'true');
  } else if (item.dataset.cqCategoryHidden === 'true') {
    delete item.dataset.cqCategoryHidden;
    item.removeAttribute('hidden');
  }
}

function cqFacetInputs(param) {
  return Array.from(document.querySelectorAll(`input[name="${param}"]`)).filter((input) => {
    if (!(input instanceof HTMLInputElement)) return false;
    const hasCanonicalTShirtCategory = document.querySelector(`input[name="${CQ_CATEGORY_FACET_PARAM}"][value="T-Shirts & Tanks"]`);
    if (param === CQ_CATEGORY_FACET_PARAM) {
      const categoryLabel = cqCategoryDisplayLabel(input);
      const shouldHide = cqIsInternalCategoryValue(categoryLabel) || !cqCategoryAllowedForContext(categoryLabel);
      cqSetCategoryInputHidden(input, shouldHide);
      if (shouldHide) return false;
      if (cqHasScopedCategoryContext()) input.closest('li')?.classList.remove('show-more-item', 'hidden');
    }
    if (param === CQ_CATEGORY_FACET_PARAM && hasCanonicalTShirtCategory && cqLegacyCategoryAliases.has(input.value)) {
      input.closest('li')?.setAttribute('hidden', 'true');
      return false;
    }
    if (param === CQ_SIZE_FACET_PARAM && cqLegacySizeAliases.has(input.value)) {
      input.closest('li')?.setAttribute('hidden', 'true');
      return false;
    }
    return true;
  });
}

function cqFacetDisplayLabel(param, value) {
  return param === CQ_SIZE_FACET_PARAM ? (cqSizeDisplayLabels.get(value) || value) : value;
}

function cqUpdateFacetLabel(input, label) {
  input.dataset.label = label;
  const labelElement = input.parentElement?.querySelector('.checkbox__label-text, .facets__pill-label, .facets__swatch-label');
  if (labelElement) labelElement.textContent = label;
}

function cqAppendHiddenAlias(form, param, value, dataKey) {
  const aliasInput = document.createElement('input');
  aliasInput.type = 'hidden';
  aliasInput.name = param;
  aliasInput.value = value;
  aliasInput.dataset[dataKey] = 'true';
  form.appendChild(aliasInput);
}

function cqPrepareFacet(param) {
  for (const input of cqFacetInputs(param)) {
    const displayLabel = param === CQ_CATEGORY_FACET_PARAM ? cqCategoryDisplayLabel(input) : cqFacetDisplayLabel(param, input.value);
    cqUpdateFacetLabel(input, displayLabel);
    if (param === CQ_CATEGORY_FACET_PARAM && input.value === CQ_CANONICAL_TSHIRT_CATEGORY && input.dataset.cqCategoryAliasesBound !== 'true') {
      input.dataset.cqCategoryAliasesBound = 'true';
      input.addEventListener('change', () => {
        const form = input.closest('form');
        if (!form) return;
        form.querySelectorAll('input[data-cq-category-alias]').forEach((aliasInput) => aliasInput.remove());
        if (!input.checked) return;
        for (const alias of cqLegacyCategoryAliases) {
          cqAppendHiddenAlias(form, CQ_CATEGORY_FACET_PARAM, alias, 'cqCategoryAlias');
        }
      });
    }
    const sizeAlias = param === CQ_SIZE_FACET_PARAM ? cqSizeAliasByCanonical.get(input.value) : null;
    if (sizeAlias && input.dataset.cqSizeAliasBound !== 'true') {
      input.dataset.cqSizeAliasBound = 'true';
      input.addEventListener('change', () => {
        const form = input.closest('form');
        if (!form) return;
        form.querySelectorAll(`input[data-cq-size-alias-for="${CSS.escape(input.value)}"]`).forEach((aliasInput) => aliasInput.remove());
        if (!input.checked) return;
        const aliasInput = document.createElement('input');
        aliasInput.type = 'hidden';
        aliasInput.name = CQ_SIZE_FACET_PARAM;
        aliasInput.value = sizeAlias;
        aliasInput.dataset.cqSizeAlias = 'true';
        aliasInput.dataset.cqSizeAliasFor = input.value;
        form.appendChild(aliasInput);
      });
    }
  }
  if (param === CQ_CATEGORY_FACET_PARAM && cqHasScopedCategoryContext()) {
    cqOrderScopedCategoryItems();
    const categoryDetails = new Set(cqFacetInputs(param).map((input) => input.closest('details')).filter(Boolean));
    for (const details of categoryDetails) {
      for (const button of details.querySelectorAll('button')) {
        if (/^\+?\s*More$/i.test(button.textContent.trim())) button.setAttribute('hidden', 'true');
      }
    }
  }
}

function cqFacetPillLabelNode(pill) {
  return Array.from(pill.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim());
}

function cqFacetPillRawLabel(pill) {
  return (cqFacetPillLabelNode(pill)?.textContent || '').trim().replace(/\s*\([^)]*\)\s*$/, '');
}

function cqSetFacetPillLabel(pill, label) {
  const labelNode = cqFacetPillLabelNode(pill);
  if (labelNode) labelNode.textContent = `
            ${label}
          `;
}

function cqNormalizeSelectedCategoryPill() {
  const selectedCategories = new URLSearchParams(window.location.search).getAll(CQ_CATEGORY_FACET_PARAM);
  const selectedLabels = new Map(selectedCategories.map((value) => [
    value,
    cqCategoryLabelOverrides.get(value) || cqCategoryServerLabelOverrides.get(value) || value,
  ]));

  for (const pill of document.querySelectorAll('facet-remove-component')) {
    const rawLabel = cqFacetPillRawLabel(pill);
    const normalizedRawLabel = cqCategoryServerLabelOverrides.get(rawLabel) || rawLabel;
    const matchingLabel = Array.from(selectedLabels.entries()).find(([value, label]) =>
      value === rawLabel || label === rawLabel || label === normalizedRawLabel
    )?.[1];
    if (matchingLabel) cqSetFacetPillLabel(pill, matchingLabel);
  }

  if (!selectedCategories.includes(CQ_CANONICAL_TSHIRT_CATEGORY)) return;
  const validLabels = new Set([CQ_CANONICAL_TSHIRT_CATEGORY, ...cqLegacyCategoryAliases]);
  const relatedPills = Array.from(document.querySelectorAll('facet-remove-component')).filter((pill) =>
    validLabels.has(cqFacetPillRawLabel(pill))
  );
  if (!relatedPills.length) return;
  const primary = relatedPills[0];
  cqSetFacetPillLabel(primary, CQ_CANONICAL_TSHIRT_CATEGORY);
  const removeUrl = new URL(window.location.href);
  removeUrl.searchParams.delete(CQ_CATEGORY_FACET_PARAM);
  primary.setAttribute('data-url', `${removeUrl.pathname}${removeUrl.search}`);
  for (const pill of relatedPills.slice(1)) pill.setAttribute('hidden', 'true');
}

function cqNormalizeSelectedColorPills() {
  const selectedColors = new Set(new URLSearchParams(window.location.search).getAll(CQ_COLOR_FACET_PARAM));
  for (const pill of document.querySelectorAll('facet-remove-component')) {
    const label = cqFacetPillRawLabel(pill);
    if (selectedColors.has(label)) cqSetFacetPillLabel(pill, label);
  }
}

function cqNormalizeSelectedSizePills() {
  const selectedSizes = new URLSearchParams(window.location.search).getAll(CQ_SIZE_FACET_PARAM);
  const selectedLabels = new Map(selectedSizes.map((value) => [value, cqFacetDisplayLabel(CQ_SIZE_FACET_PARAM, value)]));
  for (const pill of document.querySelectorAll('facet-remove-component')) {
    const rawLabel = cqFacetPillRawLabel(pill);
    if (selectedLabels.has(rawLabel)) cqSetFacetPillLabel(pill, selectedLabels.get(rawLabel));
  }
  for (const [canonical, alias] of cqSizeAliasByCanonical) {
    if (!selectedSizes.includes(canonical) || !selectedSizes.includes(alias)) continue;
    const displayLabel = cqFacetDisplayLabel(CQ_SIZE_FACET_PARAM, canonical);
    const validLabels = new Set([canonical, alias, displayLabel]);
    const relatedPills = Array.from(document.querySelectorAll('facet-remove-component')).filter((pill) =>
      validLabels.has(cqFacetPillRawLabel(pill))
    );
    if (!relatedPills.length) continue;
    const primary = relatedPills[0];
    cqSetFacetPillLabel(primary, displayLabel);
    const removeUrl = new URL(window.location.href);
    const remainingSizes = removeUrl.searchParams.getAll(CQ_SIZE_FACET_PARAM).filter((value) => value !== canonical && value !== alias);
    removeUrl.searchParams.delete(CQ_SIZE_FACET_PARAM);
    for (const value of remainingSizes) removeUrl.searchParams.append(CQ_SIZE_FACET_PARAM, value);
    primary.setAttribute('data-url', `${removeUrl.pathname}${removeUrl.search}`);
    for (const pill of relatedPills.slice(1)) pill.setAttribute('hidden', 'true');
  }
}

function cqHideAutomaticKidsAgePills() {
  const url = new URL(window.location.href);
  if ((url.searchParams.get('q') || '').trim().toLowerCase() !== 'kids') return;
  const selectedAges = new Set(url.searchParams.getAll('filter.p.m.characterquilt.age_group'));
  if (!['Youth', 'Toddler', 'Infant'].every((age) => selectedAges.has(age))) return;
  for (const pill of document.querySelectorAll('facet-remove-component')) {
    if (['Youth', 'Toddler', 'Infant'].includes(cqFacetPillRawLabel(pill))) pill.setAttribute('hidden', 'true');
  }
}


function cqNormalizeCollectionPath(pathname) {
  const normalized = String(pathname || '').replace(/\/+$/, '');
  return normalized || '/';
}

function cqShopByColumnLinks(column) {
  const seen = new Set();
  const links = [];
  for (const anchor of column.querySelectorAll('a[href*="/collections/"]')) {
    let url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (_) {
      continue;
    }
    const path = cqNormalizeCollectionPath(url.pathname);
    const label = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
    if (!label || seen.has(path)) continue;
    seen.add(path);
    url.search = '';
    url.searchParams.set('filter.v.availability', '1');
    links.push({ label, path, href: url.pathname + url.search });
  }
  return links;
}

function cqShopByCategoryLinks() {
  const currentPath = cqNormalizeCollectionPath(window.location.pathname);
  if (!currentPath.startsWith('/collections/')) return [];

  const columns = Array.from(document.querySelectorAll('.shopby__col'));

  const rootTab = Array.from(document.querySelectorAll('.shopby__tab-item')).find((item) => {
    const anchor = item.querySelector('a[href*="/collections/"]');
    if (anchor) {
      try {
        if (cqNormalizeCollectionPath(new URL(anchor.href, window.location.origin).pathname) === currentPath) return true;
      } catch (_) {}
    }
    const button = item.querySelector('[data-shopby-tab]');
    const handle = (button?.textContent || item.textContent || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return currentPath === '/collections/' + handle;
  });
  if (rootTab) {
    const panelId = rootTab.querySelector('[data-shopby-tab]')?.getAttribute('data-shopby-tab');
    const panel = panelId && document.querySelector('.shopby__panel[data-shopby-panel="' + CSS.escape(panelId) + '"]');
    if (panel) {
      return Array.from(panel.querySelectorAll('.shopby__col'))
        .flatMap((column) => {
          const columnLinks = cqShopByColumnLinks(column);
          return columnLinks[0]?.path === currentPath ? columnLinks.slice(1) : columnLinks.slice(0, 1);
        })
        .filter(Boolean);
    }
  }

  for (const column of columns) {
    const links = cqShopByColumnLinks(column);
    if (links[0]?.path === currentPath && links.length > 1) return links.slice(1);
  }

  for (const column of columns) {
    const links = cqShopByColumnLinks(column);
    if (links.slice(1).some((link) => link.path === currentPath)) return links.slice(1);
  }

  if (currentPath === '/collections/headwear') {
    const hatsLink = Array.from(document.querySelectorAll('.shopby__col a[href*="/collections/"]'))
      .map((anchor) => {
        try {
          const url = new URL(anchor.href, window.location.origin);
          const path = cqNormalizeCollectionPath(url.pathname);
          if (path !== '/collections/hats') return null;
          url.search = '';
          url.searchParams.set('filter.v.availability', '1');
          return { label: 'Hats', path, href: url.pathname + url.search };
        } catch (_) {
          return null;
        }
      })
      .find(Boolean);
    if (hatsLink) return [hatsLink];
  }

  return [];
}

function cqRenderShopByCategoryRail() {
  const links = cqShopByCategoryLinks();
  let panels = Array.from(document.querySelectorAll('details.facets__panel')).filter((panel) =>
    (panel.querySelector('summary .facets__label')?.textContent || '').trim() === 'Category'
  );

  if (links.length && !panels.length) {
    for (const wrapper of document.querySelectorAll('.facets__filters-wrapper')) {
      if (wrapper.querySelector('[data-cq-shopby-category-fallback]')) continue;
      const panel = document.createElement('details');
      panel.className = 'facets__panel';
      panel.setAttribute('data-cq-shopby-category-fallback', 'true');
      panel.innerHTML = '<summary class="facets__summary"><span class="facets__label">Category</span><span class="svg-wrapper icon-caret icon-animated" aria-hidden="true">⌄</span></summary><floating-panel-component class="facets__inputs facets__panel-content details-content"><facet-inputs-component><div class="facets__inputs-wrapper" hidden></div></facet-inputs-component></floating-panel-component>';
      wrapper.append(panel);
    }
    panels = Array.from(document.querySelectorAll('[data-cq-shopby-category-fallback]'));
  }

  for (const panel of panels) {
    const inputs = panel.querySelector('facet-inputs-component');
    if (!inputs) continue;
    const originalWrapper = inputs.querySelector('.facets__inputs-wrapper');
    let navList = inputs.querySelector('[data-cq-shopby-category-list]');

    if (!links.length) {
      if (originalWrapper) originalWrapper.hidden = false;
      navList?.remove();
      panel.removeAttribute('data-cq-shopby-category');
      continue;
    }

    panel.setAttribute('data-cq-shopby-category', 'true');
    if (originalWrapper) originalWrapper.hidden = true;
    if (!navList) {
      navList = document.createElement('ul');
      navList.className = 'facets__inputs-list list-unstyled';
      navList.setAttribute('data-cq-shopby-category-list', '');
      inputs.prepend(navList);
    }

    const signature = links.map((link) => link.label + '|' + link.href).join('||');
    if (navList.getAttribute('data-cq-signature') === signature) continue;
    navList.setAttribute('data-cq-signature', signature);
    navList.replaceChildren(...links.map((link) => {
      const item = document.createElement('li');
      item.className = 'facets__inputs-list-item';
      const checkboxId = 'cq-shopby-category-' + Math.random().toString(36).slice(2);
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.id = checkboxId;
      checkbox.className = 'cq-shopby-category-checkbox';
      checkbox.checked = link.path === cqNormalizeCollectionPath(window.location.pathname);
      checkbox.setAttribute('aria-label', link.label);
      checkbox.style.position = 'absolute';
      checkbox.style.opacity = '0';
      checkbox.style.pointerEvents = 'none';
      checkbox.addEventListener('click', (event) => event.stopPropagation());
      checkbox.addEventListener('change', (event) => {
        event.stopPropagation();
        window.location.assign(link.href);
      });

      const label = document.createElement('label');
      label.className = 'cq-shopby-category-label';
      label.htmlFor = checkboxId;
      label.style.display = 'flex';
      label.style.alignItems = 'center';
      label.style.gap = '0.5rem';
      label.style.paddingBlock = '0.45rem';
      label.style.cursor = 'pointer';
      const visualBox = document.createElement('span');
      visualBox.setAttribute('aria-hidden', 'true');
      visualBox.style.display = 'inline-flex';
      visualBox.style.alignItems = 'center';
      visualBox.style.justifyContent = 'center';
      visualBox.style.width = '18px';
      visualBox.style.height = '18px';
      visualBox.style.flex = '0 0 18px';
      visualBox.style.border = '1px solid currentColor';
      visualBox.style.borderRadius = '2px';
      visualBox.textContent = checkbox.checked ? '✓' : '';
      const labelText = document.createElement('span');
      labelText.className = 'checkbox__label-text';
      labelText.textContent = link.label;
      label.append(visualBox, labelText);
      item.append(checkbox, label);
      return item;
    }));
  }
}

function cqSetupGroupedFacets() {
  cqRenderShopByCategoryRail();
  cqNormalizeSelectedCategoryPill();
  cqNormalizeSelectedColorPills();
  cqNormalizeSelectedSizePills();
  cqHideAutomaticKidsAgePills();
  for (const param of [CQ_CATEGORY_FACET_PARAM, CQ_COLOR_FACET_PARAM, CQ_SIZE_FACET_PARAM]) {
    cqPrepareFacet(param);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', cqSetupGroupedFacets, { once: true });
} else {
  cqSetupGroupedFacets();
}

document.addEventListener(ThemeEvents.FilterUpdate, () => {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    cqSetupGroupedFacets();
  }));
});
